import { useEffect, useState } from 'react';

// Carimbado pelo vite.config.ts em cada build; o mesmo valor vai para /version.json.
declare const __BUILD_ID__: string;

const INTERVALO_CHECAGEM_MS = 60_000;
const ADIAR_MS = 15 * 60_000;

/**
 * Faixa "o sistema foi atualizado" para quem está com o Escalone aberto.
 *
 * O deploy do frontend troca os arquivos na VPS, mas a aba aberta continua com
 * o JS antigo até alguém dar F5 — e aí cada um fica numa versão, e correção que
 * já subiu "não funciona" para metade da equipe. Aqui a aba compara o próprio
 * build com o /version.json publicado e oferece recarregar.
 *
 * Não recarrega sozinha: quem está no meio de um card perderia o que digitou.
 */
const AvisoNovaVersao = () => {
  const [novaVersao, setNovaVersao] = useState(false);
  const [adiadoAte, setAdiadoAte] = useState(0);

  useEffect(() => {
    // Em dev não existe version.json: o fetch cai no index.html, o .json() falha
    // e a checagem simplesmente não acusa nada.
    if (typeof __BUILD_ID__ === 'undefined') return;

    let cancelado = false;
    const checar = async () => {
      try {
        const resp = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
        if (!resp.ok) return;
        const dados = await resp.json();
        if (!cancelado && dados?.buildId && dados.buildId !== __BUILD_ID__) {
          setNovaVersao(true);
        }
      } catch {
        // offline ou resposta não-JSON: tenta na próxima rodada
      }
    };

    const aoVoltarParaAba = () => {
      if (document.visibilityState === 'visible') checar();
    };

    checar();
    const timer = window.setInterval(checar, INTERVALO_CHECAGEM_MS);
    document.addEventListener('visibilitychange', aoVoltarParaAba);
    return () => {
      cancelado = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', aoVoltarParaAba);
    };
  }, []);

  // Reaparece sozinho quando o adiamento vence
  useEffect(() => {
    if (!adiadoAte) return;
    const t = window.setTimeout(() => setAdiadoAte(0), Math.max(0, adiadoAte - Date.now()));
    return () => window.clearTimeout(t);
  }, [adiadoAte]);

  if (!novaVersao || adiadoAte > Date.now()) return null;

  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[10200] w-[calc(100%-2rem)] max-w-md">
      <div className="flex items-center gap-3 rounded-xl bg-slate-900 text-white shadow-2xl px-4 py-3 border border-slate-700">
        <span className="relative flex h-2.5 w-2.5 shrink-0">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-400" />
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold leading-tight">O Escalone foi atualizado</p>
          <p className="text-xs text-slate-300 leading-snug">Salve o que estiver editando e atualize para usar a versão nova.</p>
        </div>
        <button
          onClick={() => setAdiadoAte(Date.now() + ADIAR_MS)}
          className="text-xs text-slate-400 hover:text-white px-2 py-1 shrink-0"
          title="Lembrar de novo em 15 minutos"
        >
          Depois
        </button>
        <button
          onClick={() => window.location.reload()}
          className="text-xs font-semibold bg-emerald-500 hover:bg-emerald-400 text-slate-900 rounded-lg px-3 py-1.5 shrink-0"
        >
          Atualizar
        </button>
      </div>
    </div>
  );
};

export default AvisoNovaVersao;
